"use client";
import {
  Button,
  Card,
  Checkbox,
  Input,
  Typography,
} from "@rahul8864/material-ui";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/Form";

import { EyeSlashIcon, EyeIcon } from "@heroicons/react/24/solid";

import { zodResolver } from "@hookform/resolvers/zod";
import Link from "next/link";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useCallback } from "react";
import { toast } from "sonner";
import { type LoginFormValues, loginSchema } from "@/schemas/Users";

const defaultValues = {
  email: "",
  password: "",
  remember: false,
};

const Login: React.FC = () => {
  const router = useRouter();
  const [loading, setLoading] = useState<boolean>(false);
  const [passwordShown, setPasswordShown] = useState(false);
  const togglePasswordVisiblity = () => setPasswordShown((cur) => !cur);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues,
  });

  const handleSubmit = useCallback(
    async (data: LoginFormValues) => {
      try {
        setLoading(true);
        await signIn("credentials", { ...data, redirect: false }).then(
          (callback) => {
            if (callback?.error) {
              setLoading(false);
              toast.error(callback.error);
            } else if (callback?.ok && !callback?.error) {
              toast.success("Logged in Successfully!");
              router.push("/");
              setLoading(false);
            }
          },
        );
      } catch (e) {
        setLoading(false);
        toast.error("Something went wrong!");
      } finally {
      }
    },
    [router],
  );

  return (
    <div className="mx-auto w-full max-w-7xl p-8">
      <div className="relative flex items-center justify-center p-8">
        <Card color="transparent" className="border p-6">
          <Typography variant="h4" color="blue-gray" className="pb-4">
            Sign In
          </Typography>
          <Form {...form}>
            <form
              className="mb-2 w-80 max-w-screen-lg border-t pt-4 sm:w-96"
              onSubmit={form.handleSubmit(handleSubmit)}
            >
              <div className="mb-1 flex flex-col gap-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Email <span className="text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          size="lg"
                          className="!border-t-blue-gray-200 placeholder:opacity-100 focus:!border-t-gray-900"
                          labelProps={{
                            className: "hidden",
                          }}
                          disabled={loading}
                          placeholder="devmobile@shop.in"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex justify-between">
                        <FormLabel>
                          Password <span className="text-red-500">*</span>
                        </FormLabel>
                        <Typography
                          as="a"
                          href="/forgot-password"
                          color="blue-gray"
                          variant="small"
                          className="font-medium"
                        >
                          Forgot password?
                        </Typography>
                        {/* <Link href='/forgot-password'>Forgot password?</Link> */}
                      </div>
                      <FormControl>
                        <Input
                          size="lg"
                          className="!border-t-blue-gray-200 placeholder:opacity-100 focus:!border-t-gray-900"
                          labelProps={{
                            className: "hidden",
                          }}
                          disabled={loading}
                          placeholder="**********"
                          {...field}
                          type={passwordShown ? "text" : "password"}
                          icon={
                            <i onClick={togglePasswordVisiblity}>
                              {passwordShown ? (
                                <EyeIcon className="size-5" />
                              ) : (
                                <EyeSlashIcon className="size-5" />
                              )}
                            </i>
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="mt-4">
                <FormField
                  control={form.control}
                  name="remember"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Checkbox
                          label={
                            <Typography
                              variant="small"
                              color="gray"
                              className="flex items-center font-normal"
                            >
                              Remember Me
                            </Typography>
                          }
                          checked={field.value}
                          onChange={field.onChange}
                          containerProps={{ className: "-ml-2.5" }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <Button
                className="mt-4 justify-center normal-case"
                fullWidth
                type="submit"
                size="lg"
                loading={loading}
                disabled={loading}
              >
                Sign In
              </Button>
              <Typography color="gray" className="mt-4 text-center font-normal">
                {"Don't have an account? "}
                <Link href="/sign-up" className="font-medium text-gray-900">
                  Sign Up
                </Link>
              </Typography>
            </form>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default Login;
